import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { Provider, useDispatch } from "react-redux";
import AppRouter from "./router/AppRouter";
import { ToastContainer } from "react-toastify";
import store from "./redux/store";
import { grey, blueGrey } from "@mui/material/colors";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  CircularProgress
} from '@mui/material';

// Auto-logout component
const AutoLogoutHandler = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);
  
  const resetAutoLogoutTimer = () => {
    // Clear existing timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    // Hide warning modal if it's showing
    setShowWarning(false);
    setCountdown(30);
    
    // Show warning after 14 minutes 30 seconds (15 minutes - 30 seconds)
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(30);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            // Auto logout when countdown reaches 0
            handleAutoLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, 14 * 60 * 1000 + 30 * 1000); // 14 minutes 30 seconds
    
    // Set the main logout timer
    timeoutRef.current = setTimeout(() => {
      handleAutoLogout();
    }, 15 * 60 * 1000); // 15 minutes
  };

  const handleAutoLogout = () => {
    // Clear all timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    // Hide warning modal
    setShowWarning(false);
    
    // Auto logout
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch({ type: 'auth/logoutSuccess' });
    delete window.axios?.defaults?.headers?.common['x-auth-token'];
    navigate('/login');
  };

  const handleStayLoggedIn = () => {
    // Clear all timers
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    // Hide warning modal
    setShowWarning(false);
    setCountdown(30);
    
    // Reset the timer
    resetAutoLogoutTimer();
  };

  const handleUserActivity = () => {
    resetAutoLogoutTimer();
  };

  useEffect(() => {
    // Set up event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown'];
    
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Start the timer
    resetAutoLogoutTimer();

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, []);

  return (
    <>
      {/* Warning Modal */}
      <Dialog 
        open={showWarning} 
        onClose={handleStayLoggedIn}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            background: 'linear-gradient(135deg, #fff 0%, #f5f5f5 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          color: '#d32f2f',
          fontWeight: 'bold',
          fontSize: '1.5rem'
        }}>
          ⚠️ Session Timeout Warning
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" sx={{ mb: 2, color: '#666' }}>
              You have been inactive for too long. You will be automatically logged out in:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: 2,
              mb: 3
            }}>
              <CircularProgress 
                variant="determinate" 
                value={(countdown / 30) * 100} 
                size={60}
                sx={{ 
                  color: countdown <= 10 ? '#d32f2f' : countdown <= 20 ? '#f57c00' : '#4caf50'
                }}
              />
              <Typography 
                variant="h3" 
                sx={{ 
                  fontWeight: 'bold',
                  color: countdown <= 10 ? '#d32f2f' : countdown <= 20 ? '#f57c00' : '#4caf50'
                }}
              >
                {countdown}s
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#888', fontStyle: 'italic' }}>
              Click "Stay Logged In" to continue your session
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={handleStayLoggedIn}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
              color: 'white',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(135deg, #45a049 0%, #4caf50 100%)',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
              }
            }}
          >
            Stay Logged In
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Create a wrapper component to use the hook
const AppContent = () => {
  return (
    <Router>
      <AutoLogoutHandler />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppRouter />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
};

function App() {
  const theme = createTheme({
    palette: {
      primary: {
        main: grey["900"],
      },
      secondary: {
        main: blueGrey["900"],
      },
    },
  });
  
  return (
    <>
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <AppContent />
        </Provider>
        <ToastContainer />
      </ThemeProvider>
    </>
  );
}

export default App;
